import styled from '@nykaa/ui-components/styles/styled';
import ImageV2 from 'shared/components/ImageV2';

// Compare Button Container (for absolute positioning)
export const CompareButtonContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 12px;
  z-index: 8;
  @media (max-width: 768px) {
    position: relative;
    right: -4px;
  }
`;

// Compare Button (floating icon style)
export const CompareButton = styled.button`
  ${({ theme }) => `
    background: ${theme.colors.surface};
    color: ${theme.colors.textPrimary};
    border: none;
    border-radius: ${theme.borders.radius40};
    padding: ${theme.spacing.spacing40};
    font-size: ${theme.typography.bodySmall.fontSize};
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.spacing20};
    transition: all 0.2s ease;
    box-shadow: ${theme.elevations.shadow300};
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.8);

    svg {
      width: 20px;
      height: 20px;
    }

    span {
      white-space: nowrap;
      font-size: ${theme.typography.bodySmall.fontSize};
    }

    @media (max-width: 768px) {
      padding: ${theme.spacing.spacing40};
      
      svg {
        width: 18px;
        height: 18px;
      }

      span {
        display: none;
      }
    }
  `}
`;

// Modal Overlay
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

// Selection Modal
export const SelectionModal = styled.div`
  ${({ theme }) => `
    background: ${theme.colors.surface};
    border-radius: ${theme.borders.radius40};
    padding: ${theme.spacing.spacing120};
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    position: relative;
    box-shadow: ${theme.elevations.shadow400};
    animation: slideUp 0.3s ease;

    @keyframes slideUp {
      from {
        transform: translateY(20px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0;
      position: fixed;
      bottom: 0;
      max-height: 90vh;
      animation: slideUpMobile 0.3s ease;

      @keyframes slideUpMobile {
        from {
          transform: translateY(100%);
        }
        to {
          transform: translateY(0);
        }
      }
    }
  `}
`;

// Comparison Modal (for results)
export const ComparisonModal = styled.div`
  ${({ theme }) => `
    background: ${theme.colors.surface};
    border-radius: ${theme.borders.radius40};
    max-width: 768px;
    width: 90%;
    max-height: 80vh;
    position: relative;
    box-shadow: ${theme.elevations.shadow400};
    animation: slideUp 0.3s ease;
    display: flex;
    flex-direction: column;

    @keyframes slideUp {
      from {
        transform: translateY(20px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
      border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0;
      position: fixed;
      bottom: 0;
      max-height: 90vh;
      animation: slideUpMobile 0.3s ease;

      @keyframes slideUpMobile {
        from {
          transform: translateY(100%);
        }
        to {
          transform: translateY(0);
        }
      }
    }
  `}
`;

// Modal Header
export const ModalHeader = styled.div`
  ${({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing.spacing80};

    h2 {
      margin: 0;
      font-size: ${theme.typography.titleLarge.fontSize};
      font-weight: 600;
      color: ${theme.colors.textPrimary};
    }
  `}
`;

// Comparison Header
export const ComparisonHeader = styled.div`
  ${({ theme }) => `
    display: grid;
    grid-template-columns: 100px 1fr 100px;
    align-items: center;
    padding: ${theme.spacing.spacing120} ${theme.spacing.spacing120} ${theme.spacing.spacing60};
    border-bottom: 1px solid ${theme.colors.surface30};
    flex-shrink: 0;
    background: ${theme.colors.surface};
    border-radius: ${theme.borders.radius40} ${theme.borders.radius40} 0 0;
    display: flex;
    justify-content: space-between;
    h2 {
      margin: 0;
      font-size: ${theme.typography.titleLarge.fontSize};
      font-weight: 600;
      color: ${theme.colors.textPrimary};
      text-align: center;
    }

    @media (max-width: 768px) {
      position: sticky;
      top: 0;
      z-index: 100;
      padding: ${theme.spacing.spacing80} ${theme.spacing.spacing80} ${theme.spacing.spacing60};
      grid-template-columns: 60px 1fr 60px;
      
      h2 {
        font-size: ${theme.typography.titleMedium.fontSize};
      }
    }
  `}
`;

// Comparison Content
export const ComparisonContent = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.spacing80};
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${theme.spacing.spacing80} ${theme.spacing.spacing120} ${theme.spacing.spacing120};
    flex: 1;

    @media (max-width: 768px) {
      padding: ${theme.spacing.spacing60} ${theme.spacing.spacing80} ${theme.spacing.spacing80};
    }
  `}
`;

// Product Slot (for selection)
export const ProductSlot = styled.div<{ isCurrentProduct?: boolean }>`
  ${({ theme, isCurrentProduct }) => `
    border-radius: ${theme.borders.radius40};
    margin-bottom: ${theme.spacing.spacing60};
    background: ${theme.colors.surface};
    border: 1px solid ${isCurrentProduct ? theme.colors.textDecorative : theme.colors.surface30};
    position: relative;
    overflow: visible;

    ${
      isCurrentProduct
        ? `
      &::after {
        content: 'Current Product';
        position: absolute;
        top: 8px;
        right: 8px;
        background: ${theme.colors.primary};
        color: ${theme.colors.textInversePrimary};
        border-radius: ${theme.borders.radius20};
        padding: ${theme.spacing.spacing10} ${theme.spacing.spacing20};
        font-size: 9px;
        font-weight: 600;
        z-index: 1;
      }
    `
        : ''
    }
  `}
`;

export const ProductSlotRow = styled.div`
  display: flex;
  align-items: stretch;
`;

export const ProductSlotImage = styled.div`
  flex-shrink: 0;
  width: 80px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

// Product Image
export const ProductImage = styled.img`
  ${({ theme }) => `
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: ${theme.borders.radius20};
    margin-right: ${theme.spacing.spacing60};
  `}
`;

// Product Info
export const ProductInfo = styled.div`
  ${({ theme }) => `
    flex: 1;
    min-width: 0;
    overflow: hidden;
    padding: ${theme.spacing.spacing60};

    h3 {
      margin: 0 0 4px 0;
      font-size: ${theme.typography.bodyMedium.fontSize};
      font-weight: 600;
      color: ${theme.colors.textPrimary};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    p {
      margin: 0;
      font-size: ${theme.typography.bodySmall.fontSize};
      color: ${theme.colors.textSecondary};
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    p:last-child {
      margin-top: 4px;
      font-weight: 700;
      color: ${theme.colors.textPrimary};
    }
  `}
`;

// Input Group
export const InputGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

// Input
export const Input = styled.input`
  ${({ theme }) => `
    flex: 1;
    border: none;
    font-size: ${theme.typography.bodyMedium.fontSize};
    color: ${theme.colors.textPrimary};
    background: transparent;
    padding: ${theme.spacing.spacing40} ${theme.spacing.spacing60};
    &:focus {
      outline: none;
    }

    &::placeholder {
      color: ${theme.colors.textSecondary};
    }
  `}
`;

// Add Button
export const AddButton = styled.button`
  ${({ theme }) => theme.typography.buttonSmall}
  ${({ theme }) => `
    background: ${theme.colors.textDecorative};
    cursor: pointer;
    border: none;
    border-radius: 0 ${theme.borders.radius60} ${theme.borders.radius60} 0;
    text-align: center;
    padding: ${theme.spacing.spacing60} ${theme.spacing.spacing120};
    &:disabled {
      background: ${theme.colors.surface30};
      color: ${theme.colors.textDisabled};
      cursor: not-allowed;
    }
  `}
`;

// Compare Button (in modal)
export const CompareButtonAction = styled.button`
  ${({ theme }) => `
    width: 100%;
    background: ${theme.colors.textPrimary};
    color: ${theme.colors.white};
    border: none;
    border-radius: ${theme.borders.radius20};
    padding: 12px;
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: 700;
    cursor: pointer;
    margin-top: ${theme.spacing.spacing80};

    &:hover:not(:disabled) {
      background: ${theme.colors.primary};
    }

    &:disabled {
      background: ${theme.colors.surface30};
      color: ${theme.colors.textDisabled};
      cursor: not-allowed;
    }
  `}
`;

// Close Button
export const CloseButton = styled.button`
  ${({ theme }) => `
    background: none;
    border: none;
    cursor: pointer;
    padding: ${theme.spacing.spacing20};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme.colors.textSecondary};
    transition: color 0.2s ease;

    &:hover {
      color: ${theme.colors.textPrimary};
    }

    svg {
      width: 24px;
      height: 24px;
    }
  `}
`;

// Back Button
export const BackButton = styled.button`
  ${({ theme }) => `
    background: none;
    border: none;
    cursor: pointer;
    padding: ${theme.spacing.spacing20};
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing.spacing20};
    color: ${theme.colors.textSecondary};
    transition: all 0.2s ease;
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: 600;

    &:hover {
      color: ${theme.colors.primary};
    }

    svg {
      width: 20px;
      height: 20px;
    }

    span {
      @media (max-width: 768px) {
        display: none;
      }
    }
  `}
`;

// Loading Spinner
export const LoadingSpinner = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${theme.spacing.spacing120};

    .spinner {
      width: 40px;
      height: 40px;
      border: 3px solid ${theme.colors.surface30};
      border-top-color: ${theme.colors.primary};
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  `}
`;

// Loading Text
export const LoadingText = styled.div`
  ${({ theme }) => `
    text-align: center;
    padding: ${theme.spacing.spacing80};
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.bodyMedium.fontSize};
  `}
`;

// Error Message
export const ErrorMessage = styled.div`
  ${({ theme }) => `
    background: ${theme.colors.negative10};
    color: ${theme.colors.negative};
    padding: ${theme.spacing.spacing60};
    border-radius: ${theme.borders.radius20};
    margin-bottom: ${theme.spacing.spacing60};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.spacing40};
    font-size: ${theme.typography.bodyMedium.fontSize};

    svg {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }
  `}
`;

// Empty Slot
export const EmptySlot = styled.div`
  ${({ theme }) => `
    border: 2px dashed ${theme.colors.surface30};
    border-radius: ${theme.borders.radius20};
    padding: ${theme.spacing.spacing120};
    text-align: center;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.bodyMedium.fontSize};
  `}
`;

// Product Columns (for comparison result display)
export const ProductColumns = styled.div<{ isMobile?: boolean }>`
  ${({ theme, isMobile }) => `
    display: ${isMobile ? 'flex' : 'grid'};
    ${
      isMobile
        ? `
      overflow-x: auto;
      gap: ${theme.spacing.spacing60};
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      padding-bottom: ${theme.spacing.spacing60};
    `
        : `
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: ${theme.spacing.spacing80};
    `
    }
  `}
`;

// Product Column
export const ProductColumn = styled.div<{ isSticky?: boolean }>`
  ${({ theme, isSticky }) => `
    flex-shrink: 0;
    min-width: ${isSticky ? '200px' : '250px'};
    scroll-snap-align: start;
    text-align: center;
    padding: ${theme.spacing.spacing60};
    border-radius: ${theme.borders.radius20};
    background: ${theme.colors.surface10};

    ${
      isSticky
        ? `
      position: sticky;
      left: 0;
      z-index: 10;
      background: ${theme.colors.surface};
      box-shadow: ${theme.elevations.shadow200};
    `
        : ''
    }

    img {
      width: 120px;
      height: 120px;
      object-fit: cover;
      border-radius: ${theme.borders.radius20};
      margin-bottom: ${theme.spacing.spacing40};
    }

    h3 {
      font-size: ${theme.typography.bodyMedium.fontSize};
      font-weight: 600;
      margin: 0 0 ${theme.spacing.spacing20} 0;
      color: ${theme.colors.textPrimary};
      line-height: 1.4;
    }

    .price {
      font-size: ${theme.typography.titleSmall.fontSize};
      font-weight: 700;
      color: ${theme.colors.textPrimary};
      margin-bottom: ${theme.spacing.spacing20};
    }

    .rating {
      font-size: ${theme.typography.bodySmall.fontSize};
      color: ${theme.colors.textSecondary};
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${theme.spacing.spacing20};
    }
  `}
`;

// Comparison Table
export const ComparisonTable = styled.div`
  ${({ theme }) => `
    width: 100%;
    border-top: 1px solid ${theme.colors.surface30};
    
    @media (max-width: 768px) {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      position: relative;
      
      &::-webkit-scrollbar {
        height: 3px;
      }
      
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      
      &::-webkit-scrollbar-thumb {
        background: ${theme.colors.surface30};
        border-radius: 3px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: ${theme.colors.textSecondary};
      }
    }
  `}
`;

// Comparison Row
export const ComparisonRow = styled.div<{
  isMobile?: boolean;
  isHeader?: boolean;
  productCount?: number;
  isDifferent?: boolean;
}>`
  ${({ theme, isMobile, isHeader, productCount = 3, isDifferent }) => `
    ${theme.typography.bodySmall};
    display: grid;
    grid-template-columns: ${isMobile ? '120px' : '200px'} repeat(${productCount}, ${
    isMobile ? '160px' : '1fr'
  });
    min-height: ${isHeader ? '60px' : '50px'};

    &:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.surface30};
    }
    ${
      isHeader
        ? `
      background: ${theme.colors.surfaceInverse};
    `
        : ''
    }

    ${
      isDifferent && !isHeader
        ? `
      background: #FFF8E1;
    `
        : ''
    }

    @media (max-width: 768px) {
      min-width: ${120 + productCount * 160}px;
    }
  `}
`;

// Feature Label
export const FeatureLabel = styled.div<{ isHeader?: boolean; isDifferent?: boolean }>`
  ${({ theme, isHeader, isDifferent }) => `
    padding: ${theme.spacing.spacing60} ${theme.spacing.spacing80};
    font-weight: ${isHeader ? '700' : '600'};
    background: ${isDifferent ? '#FFF8E1' : theme.colors.surface};
    font-size: ${theme.typography.bodyMedium.fontSize};
    text-align: left;
    border-right: 1px solid ${theme.colors.surface30};
    display: flex;
    align-items: center;
    color: ${theme.colors.textPrimary};
    @media (max-width: 768px) {
      min-width: 120px;
      padding: ${theme.spacing.spacing40} ${theme.spacing.spacing60};
      background: ${isDifferent ? '#FFF8E1' : theme.colors.surface};
    }
  `}
`;

// Feature Value
export const FeatureValue = styled.div<{ isHeader?: boolean; isDifferent?: boolean }>`
  ${({ theme, isHeader, isDifferent }) => `
    padding: ${theme.spacing.spacing80};
    font-size: ${
      isHeader ? theme.typography.bodyMedium.fontSize : theme.typography.bodySmall.fontSize
    };
    font-weight: ${isHeader ? '700' : '400'};
    color: ${isHeader ? theme.colors.textPrimary : theme.colors.textSecondary};
    text-align: center;
    line-height: 1.5;
    word-break: break-word;
    background: ${isDifferent ? '#FFF8E1' : theme.colors.surface};
    border-right: 1px solid ${theme.colors.surface30};
    overflow: hidden;
    text-overflow: ellipsis;
    &:last-child {
      border-right: none;
    }

    b {
      font-weight: 700;
      color: ${theme.colors.textPrimary};
    }

    @media (max-width: 768px) {
      min-width: 160px;
      padding: ${theme.spacing.spacing80};
      font-size: ${theme.typography.bodySmall.fontSize};
    }
  `}
`;

// Pros and Cons Section
export const ProConsSection = styled.div`
  ${({ theme }) => `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: ${theme.spacing.spacing80};
    padding: ${theme.spacing.spacing80};
    background: ${theme.colors.surface20};
    border-radius: ${theme.borders.radius20};

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      padding: ${theme.spacing.spacing60};
    }
  `}
`;

// Pros/Cons Card
export const ProConsCard = styled.div`
  ${({ theme }) => `
    background: ${theme.colors.surface};
    padding: ${theme.spacing.spacing60};
    border-radius: ${theme.borders.radius20};

    h4 {
      margin: 0 0 ${theme.spacing.spacing40} 0;
      font-size: ${theme.typography.bodyMedium.fontSize};
      font-weight: 600;
      color: ${theme.colors.textPrimary};
      display: flex;
      align-items: center;
      gap: ${theme.spacing.spacing20};
    }

    ul {
      margin: 0;
      padding-left: ${theme.spacing.spacing80};
      list-style-type: none;

      li {
        margin-bottom: ${theme.spacing.spacing40};
        font-size: ${theme.typography.bodySmall.fontSize};
        color: ${theme.colors.textSecondary};
        position: relative;
        padding-left: ${theme.spacing.spacing20};

        &::before {
          content: '•';
          position: absolute;
          left: -${theme.spacing.spacing60};
          color: ${theme.colors.primary};
        }
      }
    }
  `}
`;

// New Detailed Product Card Styles
export const ProductCardsGrid = styled.div<{ isMobile?: boolean; productCount?: number }>`
  ${({ theme }) => `
    display: flex;
    gap: ${theme.spacing.spacing60};
    padding-bottom: ${theme.spacing.spacing20};
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    flex-shrink: 0;
    
    &::-webkit-scrollbar {
      height: 3px;
    }
    
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.surface30};
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: ${theme.colors.textSecondary};
    }
  `}
`;

export const DetailedProductCard = styled.div<{ isSticky?: boolean; productCount?: number }>`
  ${({ theme, productCount }) => `
    background: ${theme.colors.surface};
    border: 1px solid ${theme.colors.surface30};
    border-radius: ${theme.borders.radius20};
    overflow: hidden;
    transition: all 0.2s ease;
    flex: 0 0 calc(${productCount ? 100 / productCount : 33}% - ${theme.spacing.spacing60});
    min-width: 160px;
    max-width: 200px;
    scroll-snap-align: start;
    display: flex;
    flex-direction: column;
    @media (min-width: 768px) {
      max-width: 100%;
    }
  `}
`;

export const CardImageWrapper = styled.div`
  ${({ theme }) => `
    width: 100%;
    aspect-ratio: 3 / 4;
    overflow: hidden;
    background: ${theme.colors.surface10};
    flex-shrink: 0;
    max-height: 200px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top;
      display: block;
    }
  `}
`;

export const ProductCardImage = styled(ImageV2)`
  @media (max-width: 768px) {
    max-height: 250px;
    aspect-ratio: 3 / 4;
  }
`;

export const ProductCardInfo = styled.div`
  ${({ theme }) => `
    padding: ${theme.spacing.spacing40} ${theme.spacing.spacing60};
    flex: 1;
    display: flex;
    flex-direction: column;
    
    @media (max-width: 768px) {
      padding: ${theme.spacing.spacing40};
    }
  `}
`;

export const ProductCardTitle = styled.div`
  ${({ theme }) => `
    margin: 0 0 ${theme.spacing.spacing10} 0;
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.bodySmall.fontSize};
    font-weight: 600;
    line-height: 1.3;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  `}
`;

export const ProductCardBrand = styled.div`
  ${({ theme }) => `
    font-size: 11px;
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.spacing20};
  `}
`;

export const PriceSection = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacing.spacing10};
    margin-bottom: ${theme.spacing.spacing20};
    flex-wrap: wrap;
  `}
`;

export const CurrentPrice = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.bodySmall.fontSize};
    font-weight: 700;
    color: ${theme.colors.textPrimary};
  `}
`;

export const OriginalPrice = styled.span`
  ${({ theme }) => `
    font-size: 11px;
    color: ${theme.colors.textSecondary};
    text-decoration: line-through;
  `}
`;

export const DiscountBadge = styled.span`
  ${({ theme }) => `
    background: ${theme.colors.positive10};
    color: ${theme.colors.positive};
    padding: 1px ${theme.spacing.spacing20};
    border-radius: ${theme.borders.radius10};
    font-size: 10px;
    font-weight: 600;
  `}
`;

export const RatingSection = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacing.spacing10};
    font-size: 11px;
    color: ${theme.colors.textSecondary};
    margin-bottom: ${theme.spacing.spacing20};

    span:first-child {
      color: ${theme.colors.textPrimary};
      font-weight: 600;
    }
  `}
`;

export const AttributeList = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.spacing40};
    margin-bottom: ${theme.spacing.spacing80};
  `}
`;

export const AttributeItem = styled.div`
  ${({ theme }) => `
    font-size: ${theme.typography.bodySmall.fontSize};
    color: ${theme.colors.textSecondary};
    line-height: 1.5;

    strong {
      color: ${theme.colors.textPrimary};
      font-weight: 600;
      margin-right: ${theme.spacing.spacing20};
    }
  `}
`;

export const ViewDetailsButton = styled.button`
  ${({ theme }) => `
    width: 100%;
    background: ${theme.colors.surface10};
    color: ${theme.colors.textPrimary};
    border: 1px solid ${theme.colors.surface30};
    border-radius: ${theme.borders.radius20};
    padding: ${theme.spacing.spacing50} ${theme.spacing.spacing60};
    font-size: ${theme.typography.bodyMedium.fontSize};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: ${theme.colors.primary};
      color: ${theme.colors.textInversePrimary};
      border-color: ${theme.colors.primary};
    }
  `}
`;

export const AddToBag = styled.button`
  ${({ theme }) => `
    width: 100%;
    height: 24px;
    background: ${theme.colors.textPrimary};
    color: ${theme.colors.white};
    border: none;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
    margin-top: auto;

    &:hover {
      background: ${theme.colors.primary};
    }

    &:active {
      transform: scale(0.98);
    }
    @media (min-width: 768px) {
      height: 44px;
      margin-top: ${theme.spacing.spacing20};
      border-radius: ${theme.borders.radius20};
    }
  `}
`;

export const SectionTitle = styled.h3`
  ${({ theme }) => `
    font-size: ${theme.typography.titleLarge.fontSize};
    font-weight: 700;
    color: ${theme.colors.textPrimary};
    margin: 0;

    @media (max-width: 768px) {
      font-size: ${theme.typography.titleMedium.fontSize};
    }
  `}
`;

// Table Section
export const TableSection = styled.div`
  ${({ theme }) => `
    margin-bottom: ${theme.spacing.spacing120};
  `}
`;

// Update ComparisonRow to accept isMobile prop
export const ComparisonRowWrapper = styled.div<{ isMobile?: boolean }>`
  ${({ isMobile }) => `
    ${
      isMobile
        ? `
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
    `
        : ''
    }
  `}
`;

// --- New components for category-based comparison ---

// Filter bar above category tables
export const FilterBar = styled.div`
  ${({ theme }) => `
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid ${theme.colors.surface30};
    padding-top: ${theme.spacing.spacing60};
    @media (max-width: 768px) {
      flex-direction: row;
      align-items: center;
      gap: ${theme.spacing.spacing40};
    }
  `}
`;

// Toggle button for "Differences Only"
export const ToggleButton = styled.button<{ active?: boolean }>`
  ${({ theme, active }) => `
    background: ${active ? theme.colors.primary : theme.colors.surface10};
    color: ${active ? theme.colors.textInversePrimary : theme.colors.textPrimary};
    border: 1px solid ${active ? theme.colors.primary : theme.colors.surface30};
    border-radius: 20px;
    padding: ${theme.spacing.spacing20} ${theme.spacing.spacing80};
    font-size: ${theme.typography.bodySmall.fontSize};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:active {
      transform: scale(0.97);
    }
  `}
`;

// Category section wrapper
export const CategorySection = styled.div`
  ${({ theme }) => `
    border: 1px solid ${theme.colors.surface30};
    border-radius: ${theme.borders.radius20};
    overflow: visible;
    box-shadow: ${theme.elevations.shadow100};
    margin-bottom: ${theme.spacing.spacing40};
  `}
`;

// Category header (collapsible)
export const CategoryHeader = styled.button`
  ${({ theme }) => `
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.spacing40};
    padding: ${theme.spacing.spacing60} ${theme.spacing.spacing80};
    background: ${theme.colors.surface};
    border: none;
    border-radius: ${theme.borders.radius20} ${theme.borders.radius20} 0 0;
    cursor: pointer;
    transition: background 0.2s ease;

    &:active {
      background: ${theme.colors.surface10};
    }

    @media (max-width: 768px) {
      padding: ${theme.spacing.spacing50} ${theme.spacing.spacing60};
    }
  `}
`;

// Category title text
export const CategoryTitle = styled.span`
  ${({ theme }) => `
    flex: 1;
    text-align: left;
    font-size: ${theme.typography.titleSmall.fontSize};
    font-weight: 700;
    color: ${theme.colors.textPrimary};

    @media (max-width: 768px) {
      font-size: ${theme.typography.bodyMedium.fontSize};
    }
  `}
`;

// Category attribute count badge
export const CategoryCount = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.bodySmall.fontSize};
    color: ${theme.colors.textSecondary};
    white-space: nowrap;
  `}
`;

// Chevron icon for expand/collapse
export const ChevronIcon = styled.span<{ isExpanded?: boolean }>`
  ${({ theme, isExpanded }) => `
    display: flex;
    align-items: center;
    transition: transform 0.2s ease;
    transform: rotate(${isExpanded ? '180deg' : '0deg'});
    color: ${theme.colors.textSecondary};

    svg {
      width: 20px;
      height: 20px;
    }
  `}
`;

// Quick info chips on product cards
export const QuickInfoChips = styled.div`
  ${({ theme }) => `
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing.spacing10};
    margin-bottom: ${theme.spacing.spacing20};
  `}
`;

// Single chip
export const InfoChip = styled.span<{ variant?: 'positive' | 'info' }>`
  ${({ theme, variant }) => `
    display: inline-block;
    padding: 1px ${theme.spacing.spacing20};
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.4;

    ${
      variant === 'positive'
        ? `
      background: ${theme.colors.positive10};
      color: ${theme.colors.positive};
    `
        : variant === 'info'
        ? `
      background: ${theme.colors.primary10 || 'rgba(0,123,255,0.1)'};
      color: ${theme.colors.primary};
    `
        : `
      background: ${theme.colors.surface20};
      color: ${theme.colors.textSecondary};
    `
    }
  `}
`;

// Rating badge in product card
export const RatingBadge = styled.span`
  ${({ theme }) => `
    background: ${theme.colors.positive10};
    color: ${theme.colors.positive};
    padding: ${theme.spacing.spacing10} ${theme.spacing.spacing20};
    border-radius: ${theme.borders.radius10};
    font-size: ${theme.typography.bodySmall.fontSize};
    font-weight: 700;
  `}
`;

// Wrapper for the combo input (input + dropdown in same field)
export const ComboInputWrapper = styled.div`
  position: relative;
  overflow: visible;
`;

// Dropdown list that appears below the input
export const DropdownList = styled.div`
  ${({ theme }) => `
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    background: ${theme.colors.surface};
    border: 1px solid ${theme.colors.surface30};
    border-top: none;
    border-radius: 0 0 ${theme.borders.radius20} ${theme.borders.radius20};
    max-height: 240px;
    overflow-y: auto;
    box-shadow: ${theme.elevations.shadow300};

    &::-webkit-scrollbar {
      width: 3px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.surface30};
      border-radius: 3px;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: ${theme.colors.textSecondary};
    }
  `}
`;

// Single dropdown item row with image, info, price
export const DropdownItem = styled.button`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacing.spacing40};
    width: 100%;
    padding: ${theme.spacing.spacing40} ${theme.spacing.spacing60};
    border: none;
    border-bottom: 1px solid ${theme.colors.surface20 || theme.colors.surface30};
    background: ${theme.colors.surface};
    cursor: pointer;
    text-align: left;
    transition: background 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: ${theme.colors.surface10 || '#f5f5f5'};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      &:hover {
        background: ${theme.colors.surface};
      }
    }
  `}
`;

// Image thumbnail in dropdown item
export const DropdownItemImage = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
`;

// Info section in dropdown item
export const DropdownItemInfo = styled.div`
  ${({ theme }) => `
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;

    span:first-child {
      font-size: ${theme.typography.bodySmall.fontSize};
      font-weight: 600;
      color: ${theme.colors.textPrimary};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    span:last-child {
      font-size: 11px;
      color: ${theme.colors.textSecondary};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  `}
`;

// Price in dropdown item
export const DropdownItemPrice = styled.span`
  ${({ theme }) => `
    font-size: ${theme.typography.bodySmall.fontSize};
    font-weight: 700;
    color: ${theme.colors.textPrimary};
    white-space: nowrap;
    flex-shrink: 0;
  `}
`;
